<!-- Camera component using library from: https://github.com/VinceG/vue-web-cam -->
<template>
  <v-dialog v-model="showDialog" persistent max-width="1000">
    <template v-if="!hideButton" v-slot:activator="{on}" >
      <div class="pa-0">
        <v-btn
            v-if="!noBtnText"
            outlined
            :color="color"
            class="mr-3 mb-3 mb-md-0"
            v-on="on"
        >
          <v-icon left>add_a_photo</v-icon>
          <span class="body-2">{{ $t('components:camera-upload-form.btn') }}</span>
        </v-btn>
        <v-btn
            v-else
            :color="color"
            v-on="on"
            icon
        >
          <v-icon>add_a_photo</v-icon>
        </v-btn>
      </div>
    </template>
    <v-card class="pa-0">
      <v-card-title>
        <h2>{{ $t('components:camera-upload-form.title') }}</h2>
      </v-card-title>
      <v-card-text>
        <v-form ref="form">
          <v-row align="center" justify="center">
            <v-col cols="12" sm="6" xs="12">
              <WebCam
                  ref="webcam"
                  :device-id="deviceId"
                  @error="onError"
                  @cameras="onCameras"
                  @camera-change="onCameraChange"
                  height="100%"
              />
            </v-col>
            <v-col cols="12" sm="6" xs="12">
              <v-img :src="img" contain/>
            </v-col>
          </v-row>
          <v-row align="center" justify="center">
            <v-col cols="12" sm="6" xs="12">
              <v-text-field
                  outlined
                  v-model="docName"
                  :label="$t('common:document-name')"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
              />
            </v-col>
            <v-col cols="12" sm="6" xs="12">
              <v-select v-model="camera" :items="devices" item-text="label" item-value="deviceId">
                <v-item
                    v-for="device in devices"
                    :key="device.deviceId"
                    :value="device.deviceId"
                >
                  {{ device.label }}
                </v-item>
              </v-select>

              <v-btn class="mr-3 mb-3 mb-md-0" outlined :color="color" @click="onStart">
                <v-icon left>play_arrow</v-icon>
                Start
              </v-btn>
              <v-btn class="mr-3 mb-3 mb-md-0" outlined :color="color" @click="onStop">
                <v-icon left>play_disabled</v-icon>
                Stop
              </v-btn>
              <v-btn class="mr-3 mb-3 mb-md-0" outlined :color="color" @click="onCapture">
                <v-icon left>camera</v-icon>
                Snapshot
              </v-btn>
            </v-col>
            <v-col cols="12" sm="6" xs="12" class="d-flex justify-center">
              <span class="red--text" v-if="showSnapshotError">{{
                  $t('components:camera-upload-form.snap-fail')
                }}</span>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
            color="primary"
            @click="uploadDocumentation()"
        >{{ $t('buttons:save') }}
        </v-btn>
        <v-btn
            color="grey"
            @click="resetSettings()"
        >{{ $t('buttons:back') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Documentation from "../../book/data/documentation";
import Utils from "../services/utils";
import {WebCam} from "vue-web-cam";
import {ruleRequired} from "../services/rules";

export default {
  name: "CameraUploadForm",
  components: {
    WebCam
  },
  props: {
    color: String,
    hideButton: Boolean,
    noBtnText: Boolean,
    presetDocuName: String
  },
  data() {
    return {
      showDialog: false,
      showSnapshotError: false,

      docName: null,

      img: null,
      camera: null,
      deviceId: null,
      devices: [],
    }
  },
  computed: {
    device: function () {
      return this.devices.find(n => n.deviceId === this.deviceId);
    }
  },
  watch: {
    camera: function (id) {
      this.deviceId = id;
    },
    devices: function () {
      // Once we have a list select the first one
      const first = this.devices[0];
      if (first) {
        this.camera = first.deviceId;
        this.deviceId = first.deviceId;
      }
    },
    showDialog: function (state) {
      if (this.presetDocuName) {
        this.docName = this.presetDocuName;
      }

      //first check if the camera has been initialized
      if (this.$refs.webcam) {
        //afterwards check the camera and autostart or stop it
        if (state) {
          this.onStart();
        } else {
          this.onStop();
        }
      }
    }
  },
  methods: {
    uploadDocumentation() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return
      }
      let documentation = new Documentation()

      if (!this.img) {
        this.showSnapshotError = true;
        return;
      }
      this.showSnapshotError = false;

      //transform the base64 encoded image into a blob, then the blob into a file
      //have to substring the image string and remove the data-value at the beginning
      let imageBlob = Utils.base64toBlob(this.img.substring(23), "image/jpeg");
      let file = new File([imageBlob], "photo_" + Date.now() + ".jpeg", imageBlob);

      if (this.docName) {
        documentation.name = this.docName;
      }

      this.$emit("postFile", {file: file, documentation: documentation});
      this.resetSettings();
    },

    resetSettings() {
      this.showDialog = false;
      this.showSnapshotError = false;
      this.alertText = "";
      this.alertType = "";
      this.docName = "";
      this.img = null;
      this.$refs.form.resetValidation();
    },

    onCapture() {
      this.img = this.$refs.webcam.capture();
    },
    onStop() {
      this.$refs.webcam.stop();
    },
    onStart() {
      this.$refs.webcam.start();
    },
    onError(error) {
      this.$logger.error(error);
    },
    onCameras(cameras) {
      this.devices = cameras;
    },
    onCameraChange(deviceId) {
      this.deviceId = deviceId;
      this.camera = deviceId;
    },
    ruleRequired
  }
}
</script>
<style lang="scss">
  .v-dialog__content.v-dialog__content--active {
    z-index: 500 !important;
  }
</style>
